import { z } from "zod";
import type { EmbedConfigDto as OpenApiEmbedConfigDto } from "@zipper/embedded-api-client";

const buttonShapeSchema = z.enum(["square", "rounded", "pill"]);
const baseOptionsShape = {
  buttonText: z.string().optional(),
  buttonShape: buttonShapeSchema.optional(),
  hideTitle: z.boolean().optional(),
  hideSignIn: z.boolean().optional(),
};

const scheduleOptionsSchema = z
  .object({
    ...baseOptionsShape,
    headerTitle: z.string().optional(),
    defaultView: z.enum(["list", "day", "list-week", "month", "week"]).optional(),
    viewSwitcherEnabled: z.boolean().optional(),
    rowStyle: z.enum(["lines", "cards"]).optional(),
    thumbnail: z.enum(["instructor", "class", "none"]).optional(),
    capacityMode: z.enum(["all", "low-only", "none"]).optional(),
    capacityThreshold: z.number().optional(),
    startDate: z.string().optional(),
    hideFilters: z.boolean().optional(),
    hideLocationFilter: z.boolean().optional(),
    hideDateStrip: z.boolean().optional(),
    hideDuration: z.boolean().optional(),
    hideThumbnail: z.boolean().optional(),
    hideInstructor: z.boolean().optional(),
    hideLocation: z.boolean().optional(),
    hideCapacity: z.boolean().optional(),
    hideCancelled: z.boolean().optional(),
    hideListView: z.boolean().optional(),
    hideCalendarView: z.boolean().optional(),
    hideDayView: z.boolean().optional(),
    hideWeekListView: z.boolean().optional(),
    hideWeekView: z.boolean().optional(),
    filterClassIds: z.array(z.string()).optional(),
    filterInstructors: z.array(z.string()).optional(),
    filterLocations: z.array(z.string()).optional(),
    requirePhone: z.boolean().optional(),
    hideRecipientBooking: z.boolean().optional(),
    includeOneOffEvents: z.boolean().optional(),
  })
  .passthrough();

const eventsOptionsSchema = z
  .object({
    ...baseOptionsShape,
    thumbnailRatio: z.enum(["3:2", "16:9", "1:1"]).optional(),
    hideFilters: z.boolean().optional(),
    hideThumbnail: z.boolean().optional(),
    hideDateTag: z.boolean().optional(),
    hideDescription: z.boolean().optional(),
    hideLocation: z.boolean().optional(),
    hidePrice: z.boolean().optional(),
    hideRegister: z.boolean().optional(),
    filterEventIds: z.array(z.string()).optional(),
    filterInstructors: z.array(z.string()).optional(),
    filterLocations: z.array(z.string()).optional(),
    requirePhone: z.boolean().optional(),
  })
  .passthrough();

const shopOptionsSchema = z
  .object({
    ...baseOptionsShape,
    initialProductId: z.string().optional(),
    callout: z.string().optional(),
    calloutText: z.string().optional(),
    hideCallouts: z.boolean().optional(),
    hideDescription: z.boolean().optional(),
    hideButton: z.boolean().optional(),
    hideTerms: z.boolean().optional(),
    hideBillingAddress: z.boolean().optional(),
    requirePhone: z.boolean().optional(),
    hideRecipientBooking: z.boolean().optional(),
  })
  .passthrough();

const appointmentOptionsSchema = z
  .object({
    ...baseOptionsShape,
    category: z.string().optional(),
    hidePrices: z.boolean().optional(),
    hideThumbnail: z.boolean().optional(),
    filterServiceIds: z.array(z.string()).optional(),
    filterInstructors: z.array(z.string()).optional(),
    filterLocations: z.array(z.string()).optional(),
    requirePhone: z.boolean().optional(),
    hideRecipientBooking: z.boolean().optional(),
  })
  .passthrough();

const themeSchema = z
  .object({
    accent: z.string(),
    accentDeep: z.string().optional(),
    accentSoft: z.string().optional(),
    background: z.string(),
    panel: z.string(),
    lineColor: z.string(),
    headline: z.string(),
    details: z.string(),
    panelInk: z.string(),
    panelInkSoft: z.string(),
    font: z.string(),
    weight: z.number(),
    radius: z.number(),
    buttonTextColor: z.string(),
  })
  .passthrough();

const sharedConfigShape = {
  instanceId: z.string(),
  orgId: z.number().int().positive(),
  orgSlug: z.string(),
  locationId: z.string().nullable().optional(),
  theme: themeSchema,
  // The generated OpenAPI contract requires this field. Older cached payloads
  // predate it; opt-out semantics make `true` the compatibility-safe default.
  giftCardsEnabled: z.boolean().default(true),
  // Org policy, not embed config. Opt-in, so a cached payload from before the
  // field existed (or an org that never opened the setting) parses to "nothing
  // required" instead of failing validation and blanking the whole embed.
  // Org ISO-4217 currency. Optional so cached payloads from before the field
  // existed still parse; formatters fall back to USD.
  currency: z.string().optional(),
  requiredSignupFields: z
    .object({
      dateOfBirth: z.boolean().optional(),
      minimumAge: z.number().int().min(0).max(120).optional(),
      ageTimeZone: z.string().optional(),
      emergencyContact: z.boolean().optional(),
      phoneNumber: z.boolean().optional(),
    })
    .passthrough()
    .optional(),
  stripe: z
    .object({
      publishableKey: z.string().min(1),
      accountId: z.string().min(1),
      currency: z.string().min(1).optional(),
      country: z.string().length(2).optional(),
    })
    .optional(),
};

export const embedConfigSchema = z.discriminatedUnion("componentType", [
  z
    .object({
      ...sharedConfigShape,
      componentType: z.literal("schedule"),
      options: scheduleOptionsSchema,
    })
    .passthrough(),
  z
    .object({
      ...sharedConfigShape,
      componentType: z.literal("events"),
      options: eventsOptionsSchema,
    })
    .passthrough(),
  z
    .object({
      ...sharedConfigShape,
      componentType: z.literal("shop"),
      options: shopOptionsSchema,
    })
    .passthrough(),
  z
    .object({
      ...sharedConfigShape,
      componentType: z.literal("appointment"),
      options: appointmentOptionsSchema,
    })
    .passthrough(),
]);

export type ValidatedEmbedConfig = z.output<typeof embedConfigSchema>;
type OpenApiRequiredEmbedConfig = Pick<
  OpenApiEmbedConfigDto,
  "instanceId" | "componentType" | "orgId" | "orgSlug" | "giftCardsEnabled"
>;
type AssertOpenApiAssignable<T extends OpenApiRequiredEmbedConfig> = T;

/**
 * Compile-time drift check for the generated contract's required identity and
 * discriminator fields. Zod owns the stricter runtime shape for optional
 * fields because OpenAPI emits nullable optionals while the embed normalizes
 * them to omitted values.
 */
export type EmbedConfigOpenApiContract =
  AssertOpenApiAssignable<ValidatedEmbedConfig>;
